import { Entity, ThemeExposure, ThemeSchemaResponse } from '@toggle/toggle';
import React from 'react';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useThemeCompanies } from '~/hooks/use-knowledge-graph/useKnowledgeGraph';
import { ThemePerformanceSection } from '~/views/theme-overview/theme-overview-drawer/components/theme-performance-section/ThemePerformanceSection';

import { StepperSection } from '../components/stepper-section/StepperSection';
import { TopCompaniesSection } from './components/top-companies-section/TopCompaniesSection';
import * as S from './ThemeOverviewTab.styles';

const TOP_COMPANIES_LIMIT = 10;

interface ThemeOverviewTabProps {
  entity?: Entity;
  theme: ThemeExposure | { theme: ThemeSchemaResponse };
  title?: string;
}

export const ThemeOverviewTab = ({
  entity,
  theme,
  title,
}: ThemeOverviewTabProps) => {
  const {
    data = [],
    isFetching,
    isError,
    error,
  } = useThemeCompanies({ themeId: theme.theme.id });

  if (isFetching) {
    return <S.LoadingSkeleton />;
  }

  if (isError) {
    throw new Error(error.message);
  }

  const entityTags = data
    .map(item => item.company)
    .slice(0, TOP_COMPANIES_LIMIT);
  const slicedData = data.slice(0, TOP_COMPANIES_LIMIT);
  const remainingCount = Math.max(0, data.length - TOP_COMPANIES_LIMIT);

  return (
    <S.Root>
      <StepperSection
        entity={entity}
        theme={theme as ThemeExposure}
        title={title}
        activeIndex={1}
        description={theme.theme.description}
      />
      <ErrorBoundaryWrapper>
        <ThemePerformanceSection
          entityTags={entityTags}
          themeId={theme.theme.id}
        />
      </ErrorBoundaryWrapper>
      {!!slicedData.length && (
        <>
          <S.HorizontalDivider />
          <TopCompaniesSection
            companies={slicedData}
            remainingCount={remainingCount}
          />
        </>
      )}
    </S.Root>
  );
};
